import { useState, useEffect } from "react";
import api from "../../../../api/api";
import moment from "moment";
import { Backdrop, CircularProgress, Skeleton } from "@mui/material";
import { getPracticeId } from "../../../../config/practice";

const PreviousReadings = ({ data }) => {
  const practiceid = getPracticeId();

  const [previousVitals, setPreviousVitals] = useState([]);
  const [isLoadingVitals, setIsLoadingVitals] = useState(false);
  const [showAllVitals, setShowAllVitals] = useState(false);

  const cardWidth = "w-[160px]"; // adjust if needed

  useEffect(() => {
    if (data?.patientid) {
      fetchpreviousvitals();
    }
  }, [data?.patientid]);

  const fetchpreviousvitals = async () => {
    try {
      setIsLoadingVitals(true);

      const response = await api.post(
        `/v1/${practiceid}/vitals/patient/${data.patientid}/vitalscurrent`
      );

      if (response.status === 200) {
        setPreviousVitals(response?.data || []);
      } else {
        setPreviousVitals([]);
      }
    } catch (error) {
      console.log(error);
      setPreviousVitals([]);
    } finally {
      setIsLoadingVitals(false);
    }
  };

const calculateBMI = (w, unit, ft, inch) => {
  const weightNum = parseFloat(w);
  const heightInches =
    parseFloat(ft || 0) * 12 + parseFloat(inch || 0);

  if (!weightNum || !heightInches) return "";

  const meters = heightInches * 0.0254;

  const kg =
    unit === "lbs"
      ? weightNum * 0.45359237
      : unit === "stone"
      ? weightNum * 6.35029
      : weightNum;

  return (kg / (meters * meters)).toFixed(1);
};
  const formatVitalsToCards = (vitals) => {
    const cards = [];

    const bmiValue = calculateBMI(
      vitals?.weight,
      vitals?.weight_cel,
      vitals?.height_ft,
      vitals?.height_in
    );

    if (vitals.height_ft || vitals.height_in) {
      cards.push({
        title: "HEIGHT",
        value: `${vitals.height_ft || ""}'${vitals.height_in || ""}"`,
        priority: 3,
      });
    }

    if (vitals.weight) {
      cards.push({
        title: "WEIGHT",
        value: vitals.weight,
        subValue: vitals.weight_cel || "",
        priority: 4,
      });
    }

    if (vitals.temp) {
      cards.push({
        title: "TEMP",
        value: vitals.temp,
        subValue: vitals.temp_cel || "°F",
        priority: 5,
      });
    }

    if (vitals.plus_bpm) {
      cards.push({
        title: "PULSE",
        value: vitals.plus_bpm,
        subValue: "bpm",
        priority: 2,
      });
    }

    if (vitals.bp_mm && vitals.bp_hgg) {
      cards.push({
        title: "BP",
        value: `${vitals.bp_mm}/${vitals.bp_hgg}`,
        subValue: "mmHg",
        priority: 1,
      });
    }

    if (vitals.o2_1 || vitals.o2_2) {
      cards.push({
        title: "O₂ SAT",
        value: vitals.o2_1 || vitals.o2_2,
        subValue: "%",
        priority: 6,
      });
    }

    if (vitals.pain_level) {
      cards.push({
        title: "PAIN",
        value: vitals.pain_level,
        subValue: "/10",
        priority: 7,
      });
    }

    if (bmiValue) {
      cards.push({
        title: "BMI",
        value: bmiValue,
        priority: 8,
      });
    }

    if (vitals.respiratory) {
      cards.push({
        title: "RESP",
        value: vitals.respiratory,
        subValue: "bpm",
        priority: 9,
      });
    }

    if (vitals.bg) {
      cards.push({
        title: "GLUCOSE",
        value: vitals.bg,
        subValue: vitals.bg_cel || "mg/dl",
        priority: 10,
      });
    }

    return cards.sort((a, b) => a.priority - b.priority);
  };

  return (
    <>
      {/* 🔵 Backdrop Loader */}
      <Backdrop open={isLoadingVitals} sx={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div>
        {isLoadingVitals ? (
          // 🔵 Skeleton cards while loading
          <div className="flex flex-wrap gap-4 px-4 py-1">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-[160px]">
                <Skeleton variant="rounded" height={90} />
              </div>
            ))}
          </div>
        ) : previousVitals?.length > 0 ? (
          previousVitals.map((vital, idx) => {
            const cards = formatVitalsToCards(vital);

            const shouldShowMore = cards.length > 7;
            const initialCards = shouldShowMore ? cards.slice(0, 5) : cards;
            const displayCards = showAllVitals ? cards : initialCards;

            return (
              <div key={idx} className="px-4 py-2">
                {/* Header */}
                <div className="flex justify-between items-center pr-8 px-2 mb-4">
                  <div className="text-[22px] font-bold text-[#1D2951]">
                    Vitals
                  </div>
                  <div className="text-[#6A7282] text-[14px]">
                    Last updated:{" "}
                    {moment(vital.created_at).format("MM/DD/YYYY")}
                  </div>
                </div>

                {/* Cards */}
                <div className="flex flex-wrap gap-4 mb-4">
                  {displayCards.map((card, i) => (
                    <div
                      key={i}
                      className={`${cardWidth} bg-white border shadow-md rounded-[14px] px-5 pt-3 pb-2`}
                    >
                      <p className="text-[#99A1AF] text-[11px] font-bold mb-1">
                        {card.title}
                      </p>

                      <div className="flex items-end gap-1">
                        <p className="text-[#1D2951] text-xl font-bold">
                          {card.value}
                        </p>
                        {card?.subValue && (
                          <p className="text-[#6A7282] text-[13px]">
                            {card.subValue}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Show more */}
                  {!showAllVitals && shouldShowMore && (
                    <div
                      onClick={() => setShowAllVitals(true)}
                      className={`${cardWidth} bg-gray-50 border rounded-[14px] p-4 flex items-center justify-center cursor-pointer`}
                    >
                      <div className="text-xs text-gray-500">SHOW MORE</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-2 text-xs text-gray-400">
            No vitals available
          </div>
        )}
      </div>
    </>
  );
};

export default PreviousReadings;
